// Single canonical current-goal store. Persisted so the user's objective survives
// navigation, "Adjust my goal" and reload. The demo seed is only used on a fresh
// session (no stored goal) or after an explicit Reset.
const KEY = "dv_goal";

// NEW vs ADJUST objective lifecycle.
// ADJUST is the default everywhere: Plan always loads whatever goal is persisted and
// only overwrites fields the user explicitly changes. NEW is an explicit opt-in — pass
// NEW_OBJECTIVE_STATE as router `state` when navigating to /plan (Landing CTAs, Reset)
// so Plan starts from a neutral goal instead of silently resurfacing a previous one.
export const NEW_OBJECTIVE_STATE = { intent: "new" };
export const isNewObjectiveIntent = (locationState) => locationState?.intent === "new";

// date_mode distinguishes three concepts that must never be conflated:
//  - "fixed": departure_date/return_date are authoritative; nights is derived from them.
//  - "flexible_window": window_start/window_end bound the search; nights is an
//    independent value (e.g. "5 nights somewhere between Oct 2 and Oct 15" — the
//    window is NOT the stay itself).
export const DEFAULT_GOAL = {
  adults: 2,
  children: 2,
  departure: "Paris",
  destination: "open",
  geography: null,
  nights: 7,
  date_mode: "flexible_window",
  departure_date: null,
  return_date: null,
  budget: 2500,
  preferences: ["sun", "swimming pool", "checked baggage"],
  max_travel_hours: 6,
  no_overnight: true,
  window_start: "15 Oct",
  window_end: "15 Nov",
};

export const loadGoal = () => {
  try {
    const s = localStorage.getItem(KEY);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

export const saveGoal = (g) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(g));
    if (g && g.budget != null) sessionStorage.setItem("dv_budget", String(g.budget));
  } catch {
    /* ignore */
  }
};

export const clearGoal = () => {
  try {
    localStorage.removeItem(KEY);
    sessionStorage.removeItem("dv_budget");
  } catch {
    /* ignore */
  }
};

export const hasGoal = () => !!loadGoal();

// The budget carrier used by Results/Decision derives from the canonical goal.
export const goalBudget = () => {
  const g = loadGoal();
  if (g && g.budget != null) return Number(g.budget);
  try {
    return Number(sessionStorage.getItem("dv_budget") || 2500);
  } catch {
    return 2500;
  }
};


export const formatTripDate = (value, lang = "fr") => {
  if (!value) return null;
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(String(value));
  if (!iso) return String(value);
  try {
    return new Date(`${value}T12:00:00`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", {
      day: "numeric", month: "short",
    });
  } catch {
    return String(value);
  }
};

export const tripDateContext = (goal, lang = "fr") => {
  if (!goal) return null;
  if (goal.departure_date && goal.return_date) {
    return {
      kind: "fixed",
      label: `${formatTripDate(goal.departure_date, lang)} → ${formatTripDate(goal.return_date, lang)} · ${goal.nights || ""} ${lang === "fr" ? "nuits" : "nights"}`,
    };
  }
  if (goal.date_mode === "flexible_window" || goal.window_start || goal.window_end) {
    const start = formatTripDate(goal.window_start, lang);
    const end = formatTripDate(goal.window_end, lang);
    if (start && end) {
      return {
        kind: "flexible",
        label: lang === "fr"
          ? `${goal.nights} nuits dans la fenêtre ${start} → ${end}`
          : `${goal.nights} nights within ${start} → ${end}`,
      };
    }
  }
  return goal.nights ? { kind: "duration", label: lang === "fr" ? `${goal.nights} nuits` : `${goal.nights} nights` } : null;
};
